/** Phone number formatting helpers — US-friendly display, E.164-
 *  friendly storage. The API normalizes whatever shape we send via
 *  ``services/sms_verify.normalize_e164``, so the helpers here can
 *  be lenient with input and let the server be the source of truth.
 *
 *  - :func:`formatPhoneInput`: live-mask as the user types. 10
 *    digits → ``(704) 555-1234``. Numbers that look international
 *    (``+`` prefix) stay as-is so we don't mangle a ``+44`` etc.
 *  - :func:`formatPhoneForDisplay`: render a stored E.164 ``+1...``
 *    as ``(xxx) xxx-xxxx`` for read-only labels.
 */

/** Strip everything except digits and a leading ``+``. The plus is
 *  preserved because ``+44 7700 900 000`` shouldn't become
 *  ``447700900000`` and lose its country-code marker. */
export function phoneDigitsAndPlus(raw: string): string {
  if (!raw) return "";
  // Keep a leading +, then strip everything that isn't a digit.
  const hasPlus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  return hasPlus ? `+${digits}` : digits;
}

/** Live-format ``raw`` for the input field as the user types.
 *
 *  Rules:
 *  - International (anything starting with ``+``): pass through with
 *    ``+`` plus digits. We don't try to format Australian / German /
 *    etc. — too many country-specific patterns. The user's own
 *    formatting wins.
 *  - 10-digit US: ``(xxx) xxx-xxxx`` progressively as the user
 *    types. Partial inputs format to whatever's typed so far.
 *  - 11-digit US starting with 1 (``17045551234``): same display,
 *    drop the leading 1 visually but keep it in the input string so
 *    the server still sees ``+17045551234`` after normalization.
 *  - Anything else: best-effort — strip non-digits, return as-is.
 */
export function formatPhoneInput(raw: string): string {
  const cleaned = phoneDigitsAndPlus(raw);
  if (cleaned.startsWith("+")) {
    // +1 numbers (US/Canada) get rendered as the bare US mask —
    // contractors and homeowners read ``(704) 555-0100``, not
    // ``+1 (704) 555-0100``. The +1 round-trips automatically
    // because the server's normalize_e164 re-adds it on submit.
    if (cleaned.startsWith("+1") && cleaned.length > 2) {
      return formatUSDigits(cleaned.slice(2));
    }
    // True international (anything other than +1): pass through.
    // Country-specific masking is out of scope; the server still
    // normalizes whatever shape we send.
    return cleaned;
  }
  // No leading +. If 11 digits starting with 1, treat as US.
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return formatUSDigits(cleaned.slice(1));
  }
  // Plain US-style input — format whatever's there.
  return formatUSDigits(cleaned);
}

/** Format a stored phone for read-only display. Accepts E.164
 *  (``+17045551234``) or anything :func:`formatPhoneInput` would
 *  accept. Returns ``""`` for null/empty so callers can render a
 *  dash or "—" when the field is unset. */
export function formatPhoneForDisplay(stored: string | null | undefined): string {
  if (!stored) return "";
  return formatPhoneInput(stored);
}

// ---------------------------------------------------------------

/** Format up-to-10 US digits as ``(xxx) xxx-xxxx`` progressively.
 *  Truncates anything past 10 digits — the input mask shouldn't let
 *  the user type more in the first place. */
function formatUSDigits(digits: string): string {
  const d = digits.slice(0, 10);
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
